// navigation.js — single source of truth for the sidebar AND the routed pages.
// Each entry carries its icon plus the content shown on its (placeholder) page,
// so adding a nav item wires up both the menu and its screen.
import {
  HomeIcon,
  CalendarDaysIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  TagIcon,
  SparklesIcon,
  UserGroupIcon,
  HeartIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

// The AI assistant is its own section (pages/Assistant) — it spans every module,
// so it isn't nested under any one of them. Shared by both navs.
const ASSISTANT = {
  name: 'Assistant', path: '/assistant', icon: SparklesIcon,
  title: 'Assistant',
  subtitle: 'Ask or tell OmniPrac — by voice or typing.',
  features: [],
};

// -----------------------------------------------------------------------------
// Doctor (anaesthetist) navigation
// -----------------------------------------------------------------------------
export const DOCTOR_NAV = [
  {
    name: 'Dashboard', path: '/dashboard', icon: HomeIcon,
    title: 'Dashboard',
    subtitle: 'Your day at a glance — cases, alerts and outstanding actions.',
    features: [
      "Today's theatre list with induction / handover status",
      'Red-flag alerts from pre-operative assessments',
      'Unbilled cases and claims awaiting submission',
      'Payments received and outstanding balances',
    ],
  },
  ASSISTANT,
  {
    name: 'Theatre Schedule', path: '/schedule', icon: CalendarDaysIcon,
    title: 'Theatre Schedule',
    subtitle: 'One calendar across every hospital and surgical team you work with.',
    features: [
      'Cross-hospital scheduling in a single view',
      'Surgeon, procedure and session detail per slot',
      'Sync sessions from surgeons and hospital lists',
      'Conflict detection across concurrent lists',
    ],
  },
  {
    name: 'Patients', path: '/patients', icon: UserIcon,
    title: 'Patients',
    subtitle: 'Demographics, medical aid details and case history in one record.',
    features: [
      'Patient demographics and medical aid membership',
      'Medical aid number validation before the case',
      'Comorbidity and allergy history',
      'Full case and billing history per patient',
    ],
  },
  {
    name: 'Pre-Op Assessments', path: '/preop', icon: ClipboardDocumentCheckIcon,
    title: 'Pre-Operative Assessments',
    subtitle: 'No surprises on the table — assess risk days before surgery.',
    features: [
      'Send secure digital questionnaires via SMS / WhatsApp',
      'Automatic red-flag detection (BMI, cardiac, sleep apnea, allergies)',
      'Automated financial consent with digital signature',
      'Order tests and plan the anaesthetic approach in advance',
    ],
  },
  {
    name: 'Case Capture', path: '/cases', icon: ClockIcon,
    title: 'Case Capture & Time Billing',
    subtitle: 'Frictionless in-theatre capture that converts time into billing units.',
    features: [
      'One-tap start / stop from induction to patient handover',
      'Automatic conversion of minutes into billing units',
      'ICD-10 and tariff code capture with macros and templates',
      'Automatic modifiers (BMI, emergency, after-hours, neonate)',
    ],
  },
  {
    name: 'Billing & Claims', path: '/billing', icon: DocumentTextIcon,
    title: 'Billing & Claims',
    subtitle: 'Pre-emptive scrubbing before a claim ever reaches a medical aid.',
    features: [
      'Claim scrubbing: ICD-10 ↔ tariff match and required modifiers',
      'Real-time submission through clearinghouse switches',
      'Validation of medical aid membership before submission',
      'Track claim status from draft to paid',
    ],
  },
  {
    name: 'Remittances', path: '/remittances', icon: BanknotesIcon,
    title: 'Remittances',
    subtitle: 'Automated reconciliation of what the medical aids actually paid.',
    features: [
      'Auto-read Electronic Remittance Advice (ERA) files',
      'Reconcile payments against outstanding invoices',
      'Flag short-payments and rejections for follow-up',
      'Per-case paid / outstanding position',
    ],
  },
  {
    name: 'Reports', path: '/reports', icon: ChartBarIcon,
    title: 'Reports',
    subtitle: 'Understand your practice: activity, billing and collections.',
    features: [
      'Cases and units billed by period and hospital',
      'Turnaround time from case to payment',
      'Rejection and short-payment analysis',
      'Revenue by procedure and medical aid',
    ],
  },
];

// -----------------------------------------------------------------------------
// Admin (practice / bureau) navigation
// -----------------------------------------------------------------------------
export const ADMIN_NAV = [
  {
    section: 'Practice',
    name: 'Dashboard', path: '/dashboard', icon: HomeIcon,
    title: 'Practice Dashboard',
    subtitle: 'The whole practice at a glance — billing throughput and collections.',
    features: [
      'Claims submitted, paid and rejected across all doctors',
      'Cash collected vs outstanding by ageing bucket',
      'Cases awaiting coding or submission',
      'Exceptions needing administrative attention',
    ],
  },
  { section: 'Practice', ...ASSISTANT },
  {
    section: 'Practice',
    name: 'Patients', path: '/patients', icon: UserIcon,
    // Rendered by pages/Patients/Patients.jsx.
    title: 'Patients',
    subtitle: 'Demographics and medical aid details for every patient the practice bills.',
    features: [],
  },
  {
    section: 'Practice',
    name: 'Surgeons', path: '/surgeons', icon: UserGroupIcon,
    title: 'Surgeons',
    subtitle: 'The surgeons whose lists your anaesthetists cover, and their practice numbers.',
    features: [],
  },
  {
    section: 'Practice',
    name: 'Quotes & Consent', path: '/quotes', icon: ClipboardDocumentCheckIcon,
    title: 'Quotes & Consent',
    subtitle: 'Anaesthetic estimates patients accept before surgery.',
    features: [],
  },
  {
    section: 'Practice',
    name: 'Pre-op Questionnaires', path: '/preop', icon: HeartIcon,
    title: 'Pre-op Questionnaires',
    subtitle: 'Health questions patients answer before their anaesthetic.',
    features: [],
  },
  {
    section: 'Practice',
    name: 'Pre-op Questions', path: '/preop-questions', icon: PencilSquareIcon,
    title: 'Pre-op Questions',
    subtitle: 'Tailor the questions patients are asked before their anaesthetic.',
    features: [],
  },
  {
    section: 'Practice',
    name: 'Theatre Schedule', path: '/schedule', icon: CalendarDaysIcon,
    title: 'Theatre Schedule',
    subtitle: 'Practice-wide view of every doctor’s lists and sessions.',
    features: [
      'All doctors and hospitals in one calendar',
      'Session and list management',
      'Capacity and utilisation view',
      'Export lists for hospitals and surgeons',
    ],
  },
  {
    section: 'Practice',
    name: 'Billing & Claims', path: '/billing', icon: DocumentTextIcon,
    title: 'Billing & Claims',
    subtitle: 'The bureau workbench — scrub, submit and track every claim.',
    features: [
      'Bulk claim scrubbing and submission',
      'Switch / clearinghouse integration status',
      'Rejection queue with resubmission workflow',
      'Full audit trail per claim',
    ],
  },
  {
    section: 'Practice',
    name: 'Remittances', path: '/remittances', icon: BanknotesIcon,
    title: 'Remittances & Reconciliation',
    subtitle: 'Automated ERA processing across the whole practice.',
    features: [
      'Import and auto-read ERA files from all medical aids',
      'Automatic reconciliation against invoices',
      'Short-payment and rejection exception handling',
      'Daily reconciliation summary',
    ],
  },
  {
    section: 'Practice',
    name: 'Debtors & Collections', path: '/debtors', icon: ArrowTrendingUpIcon,
    title: 'Debtors & Collections',
    subtitle: 'Automated follow-up until every balance is settled.',
    features: [
      'Automated SMS / email reminders at 30 / 60 / 90 days',
      'Private-patient and medical-aid balance tracking',
      'Ageing analysis and collection workflow',
      'Payment arrangements and write-offs',
    ],
  },
  {
    section: 'Practice',
    name: 'Reports', path: '/reports', icon: ChartBarIcon,
    title: 'Practice Reports',
    subtitle: 'Financial and operational reporting for the practice.',
    features: [
      'Revenue by doctor, procedure and medical aid',
      'Collections and ageing analysis',
      'Claim rejection trends and reasons',
      'Productivity and turnaround metrics',
    ],
  },
  {
    section: 'Administration',
    name: 'User Management', path: '/users', icon: UsersIcon,
    admin: true,
    // Rendered by pages/UsersAdmin.jsx (a real CRUD screen), not ModulePage.
    title: 'User Management',
    subtitle: 'Create, edit and remove doctors and administrators for this practice.',
    features: [],
  },
  {
    section: 'Administration',
    name: 'Codes & Tariffs', path: '/codes', icon: TagIcon,
    title: 'Codes & Tariffs',
    subtitle: 'Maintain the coding and modifier engine that drives billing.',
    features: [
      'ICD-10 and anaesthetic tariff code libraries',
      'Time-unit conversion rules',
      'Modifier rules (BMI, emergency, after-hours, age)',
      'Medical aid rate / scheme configuration',
    ],
    admin: true,
  },
  {
    section: 'Administration',
    name: 'Settings', path: '/settings', icon: Cog6ToothIcon,
    title: 'Settings',
    subtitle: 'Practice configuration and integrations.',
    features: [
      'Practice details and branding',
      'Switch / clearinghouse credentials',
      'Notification templates (SMS / WhatsApp / email)',
      'Security and access policy',
    ],
    admin: true,
  },
];

export const navForRole = (role) => (role === 'admin' ? ADMIN_NAV : DOCTOR_NAV);
